
import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../lib/supabase/client';
import { useDemoMode } from '../../contexts/DemoModeContext';
import { ladeKiModell, speichereKiModell, ladeSchwelle, speichereSchwelle } from '../../lib/ki/analyseApi';
import { ladeZentraleGlaeubigerId, speichereZentraleGlaeubigerId } from '../admin/dokumente/vorbelegung';
import OrdnerWechselListe from './OrdnerWechselListe';
import { useMobilModus } from '../../lib/immobilien/mobil';
import { useViewport } from '../../components/island/hooks';
import { GlasTooltipSchicht } from '../../components/ui/GlasTooltip';
import { DemoWidget, FehlerBalken, SeitenKopf, rasterSpalten } from './teile/Rahmen';
import { PortalWidget, type PortalModulZustand } from './teile/PortalWidget';
import { KiWidget, SepaWidget } from './teile/EinstellungWidgets';
import { EpostWidget } from './teile/EpostWidget';
import { EpostKostenWidget } from './teile/EpostKostenWidget';
import { PORTAL_MODULE, portalUpdates } from '../../lib/controlCenter/logik';

const fehlerText = (err: unknown): string => (err instanceof Error ? err.message : (err as { message?: string } | null)?.message ?? String(err));


const SuperAdminDashboard: React.FC = () => {
  const { isDemoMode, toggleDemoMode, loading: demoLoading } = useDemoMode();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { mobil, schmal } = useMobilModus();
  const spalten = rasterSpalten(useViewport().width);

  // KI-Einstellungen State
  const [kiModell, setKiModell] = useState<'mistral' | 'gemini'>('mistral');
  const [kiGespeichert, setKiGespeichert] = useState(false);
  const [schwelle, setSchwelle] = useState<number>(0.8);

  // Zentrale SEPA-Gläubiger-ID der Verwaltung — greift überall dort, wo beim
  // Eigentümer keine eigene hinterlegt ist (siehe lib/signatur/quellen.ts).
  const [glaeubigerId, setGlaeubigerId] = useState('');
  const [glaeubigerGespeichert, setGlaeubigerGespeichert] = useState(false);
  // Zuletzt gespeicherter Stand — verhindert einen Schreibvorgang bei jedem Blur.
  const glaeubigerGeladen = useRef('');
  // Namen der Module, wie sie in portal_modules stehen – beim Speichern bleiben sie unverändert.
  const dbNamen = useRef<Record<string, string>>({});

  const [modules, setModules] = useState<PortalModulZustand[]>(() => PORTAL_MODULE.map((m) => ({ ...m, active: true })));

  useEffect(() => {
    fetchModuleStatus();
  }, []);

  useEffect(() => {
    ladeKiModell().then(setKiModell);
    ladeSchwelle().then(setSchwelle);
    ladeZentraleGlaeubigerId().then((v) => { glaeubigerGeladen.current = v; setGlaeubigerId(v); }).catch(console.error);
  }, []);

  const kiSpeichern = async (m: 'mistral' | 'gemini') => {
    setKiModell(m);
    try {
      await speichereKiModell(m);
      setKiGespeichert(true);
      setTimeout(() => setKiGespeichert(false), 2000);
    } catch (e) {
      console.error(e);
    }
  };

  const schwelleSpeichern = async (prozent: number) => {
    const wert = Math.min(1, Math.max(0, prozent / 100));
    setSchwelle(wert);
    try {
      await speichereSchwelle(wert);
      setKiGespeichert(true);
      setTimeout(() => setKiGespeichert(false), 2000);
    } catch (e) {
      console.error(e);
    }
  };

  const glaeubigerIdSpeichern = async () => {
    const wert = glaeubigerId.trim();
    if (wert === glaeubigerGeladen.current) return;
    try {
      await speichereZentraleGlaeubigerId(wert);
      glaeubigerGeladen.current = wert;
      setGlaeubigerGespeichert(true);
      setTimeout(() => setGlaeubigerGespeichert(false), 2000);
    } catch (e: unknown) {
      console.error(e);
      setError(fehlerText(e));
    }
  };

  const fetchModuleStatus = async () => {
    setFetching(true);
    try {
      const { data, error } = await supabase
        .from('portal_modules')
        .select('*');

      if (error) throw error;

      if (data && data.length > 0) {
        dbNamen.current = Object.fromEntries((data as { id: string; name: string | null }[])
          .filter((m) => !!m.name).map((m) => [m.id, m.name as string]));
        setModules(prev => prev.map(mod => {
          const dbMod = data.find(m => m.id === mod.id);
          return dbMod ? { ...mod, active: dbMod.is_active } : mod;
        }));
      }
    } catch (err: unknown) {
      setError(fehlerText(err));
    } finally {
      setFetching(false);
    }
  };

  const toggleModule = (id: string) => {
    setModules(prev => prev.map(m => m.id === id ? { ...m, active: !m.active } : m));
    setSuccess(false);
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    try {
      const updates = portalUpdates(modules, dbNamen.current);

      const { error } = await supabase
        .from('portal_modules')
        .upsert(updates, { onConflict: 'id' });

      if (error) throw error;

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: unknown) {
      setError(fehlerText(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col" style={{ gap: 14 }}>
      <SeitenKopf schmal={schmal || mobil} laedt={loading} gesperrt={loading || fetching} erfolg={success} onSpeichern={handleSave} />
      {error && <FehlerBalken text={error} onSchliessen={() => setError(null)} mobil={mobil} />}

      <div className="grid items-start" style={{ gridTemplateColumns: 'repeat(12, minmax(0, 1fr))', gap: 14 }}>
        <DemoWidget aktiv={isDemoMode} laedt={demoLoading} onUmschalten={() => void toggleDemoMode()} />

        <PortalWidget module={modules} laedt={fetching} spalte={spalten.gross} onUmschalten={toggleModule} />

        <KiWidget modell={kiModell} schwelle={schwelle} gespeichert={kiGespeichert} spalte={spalten.klein}
          onModell={(m) => void kiSpeichern(m)} onSchwelle={(p) => void schwelleSpeichern(p)} />

        <SepaWidget wert={glaeubigerId} gespeichert={glaeubigerGespeichert} spalte={spalten.klein}
          onAendern={setGlaeubigerId} onVerlassen={() => void glaeubigerIdSpeichern()} />

        <EpostWidget spalte={spalten.klein} />

        <EpostKostenWidget spalte={spalten.klein} />

        <OrdnerWechselListe spalte={spalten.gross} />
      </div>
      <GlasTooltipSchicht />
    </div>
  );
};


export default SuperAdminDashboard;
